import { connect } from "react-redux";
import { Col, Row } from "react-bootstrap";
import { useDebouncedCallback } from "use-debounce";
import React, { useRef, useMemo, useState, useCallback } from "react";

import AddCustomType from "./AddEditModals/AddCustomType";
import Topbar from "../../../shared/components/topbar/Topbar";
import Button from "../../../shared/components/Buttons/Button";
import { itemOrganizationActions } from "../../../redux/actions";
import AddCustomCategory from "./AddEditModals/AddCustomCategory";
import AddCustomSubCategory from "./AddEditModals/AddCustomSubCategory";
import {
  getItemOrganizationActionColumn,
  getRowStyle,
  LoadingIndicator,
} from "../../../shared/utility";
import AGGridTable from "../../../shared/components/agGrid/AGGridTable";
import SearchField from "../../../shared/components/searchField/SearchField";
import AGGridToolTip from "../../../shared/components/toolTipComponent/AGGridToolTip";
import {
  buttonNameConstants,
  buttonTypeConstants,
  itemOrganizationConstants,
} from "../../../constants";
import {
  AddIcon,
  EditIcon,
  CrossIcon,
  DeleteIcon,
  SearchIcon,
  ActionDotsIcons,
} from "../../../assets/icons/iconsProvider";
import ClickableTooltip from "../../../shared/components/toolTipComponent/ClickableTooltip";

const ItemOrganization = (props) => {
  //-------Destructure Props
  const {
    currentStore,
    itemOrganization,
    addCustomCategories,
    addCustomProductTypes,
    addCustomSubCategories,
    deleteCustomCategories,
    updateCustomCategories,
    deleteCustomProductTypes,
    updateCustomProductTypes,
    deleteCustomSubCategories,
    updateCustomSubCategories,
  } = props;

  //-------UseRef
  const gridRef = useRef();
  const tooltipRef = useRef();

  //-------Reducer
  const { customTypes, customCategories, customSubCategories } =
    itemOrganization;

  //--------UseState

  const [typeSearchText, setTypeSearchText] = useState("");
  const [typeModalFormData, setTypeModalFormData] = useState("");
  const [categorySearchText, setCategorySearchText] = useState("");
  const [isCustomTypeModal, setIsCustomTypeModal] = useState(false);
  const [isTypeSearchLoading, setIsTypeSearchLoading] = useState(false);
  const [categoryModalFormData, setCategoryModalFormData] = useState("");
  const [subCategorySearchText, setSubCategorySearchText] = useState("");
  const [isCustomCategoryModal, setIsCustomCategoryModal] = useState(false);
  const [debouncedTypeSearchText, setDebouncedTypeSearchText] = useState("");
  const [subCategoryModalFormData, setSubCategoryModalFormData] = useState("");
  const [isCategorySearchLoading, setIsCategorySearchLoading] = useState(false);
  const [debouncedCategorySearchText, setDebouncedCategorySearchText] =
    useState("");
  const [debouncedSubCategorySearchText, setDebouncedSubCategorySearchText] =
    useState("");
  const [isCustomSubCategoryModal, setIsCustomSubCategoryModal] =
    useState(false);
  const [isSubCategorySearchLoading, setIsSubCategorySearchLoading] =
    useState(false);

  const toggleCustomTypeModal = () => {
    setIsCustomTypeModal(!isCustomTypeModal);
  };

  const toggleCustomCategoryModal = () => {
    setIsCustomCategoryModal(!isCustomCategoryModal);
  };

  const toggleCustomSubCategoryModal = () => {
    setIsCustomSubCategoryModal(!isCustomSubCategoryModal);
  };

  const clearTypeSearchText = () => {
    setTypeSearchText("");
    setDebouncedTypeSearchText("");
  };

  const clearCategorySearchText = () => {
    setCategorySearchText("");
    setDebouncedCategorySearchText("");
  };
  const clearSubCategorySearchText = () => {
    setSubCategorySearchText("");
    setDebouncedSubCategorySearchText("");
  };

  const handleEditClick = (editType, props) => {
    if (props.hideTooltipCallback) {
      props.hideTooltipCallback();
    }
    switch (editType) {
      case itemOrganizationConstants.UPDATE_CUSTOM_PRODUCT_TYPES:
        toggleCustomTypeModal();
        setTypeModalFormData(props.data);
        break;
      case itemOrganizationConstants.UPDATE_CUSTOM_CATEGORIES:
        toggleCustomCategoryModal();
        setCategoryModalFormData(props.data);
        break;
      case itemOrganizationConstants.UPDATE_CUSTOM_SUB_CATEGORIES:
        toggleCustomSubCategoryModal();
        setSubCategoryModalFormData(props.data);
        break;
      default:
        return;
    }
  };

  const handleDeleteClick = (deleteType, props) => {
    switch (deleteType) {
      case itemOrganizationConstants.UPDATE_CUSTOM_PRODUCT_TYPES:
        deleteCustomProductTypes(
          currentStore?.id,
          [props.data.id],
          currentStore?.companyId
        );
        break;
      case itemOrganizationConstants.UPDATE_CUSTOM_CATEGORIES:
        deleteCustomCategories(
          currentStore?.id,
          props.data.id,
          currentStore?.companyId
        );
        break;
      case itemOrganizationConstants.UPDATE_CUSTOM_SUB_CATEGORIES:
        deleteCustomSubCategories(
          currentStore?.id,
          [props.data.id],
          currentStore?.companyId
        );
        break;
      default:
        return;
    }
  };

  function actionCellRenderer(editType, params) {
    return (
      <div
        onMouseLeave={() => {
          tooltipRef.current?.close();
        }}
      >
        <>
          {getItemOrganizationActionColumn({
            handleEditClick,
            handleDeleteClick,
            editType,
            params,
            tooltipRef,
          })}
        </>
      </div>
    );
  }

  const TypeColDefs = useMemo(
    () => [
      {
        headerName: "Custom Types",
        field: "productType",
      },
      {
        maxWidth: 100,
        headerName: "",
        field: "id",
        lockPosition: "right",
        cellRenderer: (params) =>
          actionCellRenderer(
            itemOrganizationConstants.UPDATE_CUSTOM_PRODUCT_TYPES,
            params
          ),
        // tooltipField: "id",
        cellStyle: {
          display: "flex",
          justifyContent: "end",
          alignItems: "center",
          overflow: "visible",
        },
        // tooltipComponentParams: {
        //   type: "actionTooltip",
        //   color: "#f4f4f4",
        //   className: "actionTooltip",
        //   ActionButtons: typesActionButtons,
        // },
        sortable: false,
      },
    ],
    // eslint-disable-next-line
    [customTypes]
  );

  const CategoriesColDefs = useMemo(
    () => [
      {
        headerName: "Custom Categories",
        field: "name",
        minWidth: 150,
      },
      {
        headerName: "Type",
        field: "productTypeName",
      },
      {
        maxWidth: 100,
        headerName: "",
        field: "id",
        lockPosition: "right",
        cellRenderer: (params) =>
          actionCellRenderer(
            itemOrganizationConstants.UPDATE_CUSTOM_CATEGORIES,
            params
          ),
        // tooltipField: "id",
        cellStyle: {
          display: "flex",
          justifyContent: "end",
          alignItems: "center",
          overflow: "visible",
        },
        // tooltipComponentParams: {
        //   type: "actionTooltip",
        //   color: "#f4f4f4",
        //   className: "actionTooltip",
        //   ActionButtons: categoriesActionButtons,
        // },
        sortable: false,
      },
    ], // eslint-disable-next-line
    [customCategories]
  );

  const SubCategoriesColDefs = useMemo(
    () => [
      {
        headerTooltip: "Custom Sub-Categories",
        headerName: "Custom Sub-Categories.",
        field: "name",
      },
      {
        headerName: "Type",
        field: "parentProductTypeName",
      },
      {
        maxWidth: 100,
        headerName: "",
        field: "id",
        lockPosition: "right",
        cellRenderer: (params) =>
          actionCellRenderer(
            itemOrganizationConstants.UPDATE_CUSTOM_SUB_CATEGORIES,
            params
          ),
        // tooltipField: "id",
        cellStyle: {
          display: "flex",
          justifyContent: "end",
          alignItems: "center",
          overflow: "visible",
          border: "none",
        },
        // tooltipComponentParams: {
        //   type: "actionTooltip",
        //   color: "#f4f4f4",
        //   className: "actionTooltip",
        //   ActionButtons: subCategoriesActionButtons,
        // },
        sortable: false,
      },
    ], // eslint-disable-next-line
    [customSubCategories]
  );

  const typeGetRowId = useCallback((params) => params.data.id, []);

  const defaultColDef = useMemo(() => {
    return {
      cellStyle: { display: "flex", alignItems: "center" },
      resizable: false,
      tooltipComponent: AGGridToolTip,
    };
  }, []);

  const typesGridData = useMemo(() => {
    return customTypes.filter((type) => type.isCustom === true);
  }, [customTypes]);

  const categoriesGridData = useMemo(() => {
    return customCategories.filter((type) => type.isCustom === true);
  }, [customCategories]);

  const subCategoriesGridData = useMemo(() => {
    return customSubCategories.filter((type) => type.isCustom === true);
  }, [customSubCategories]);

  const handleTypeSearchChange = (value) => {
    setIsTypeSearchLoading(true);
    setDebouncedTypeSearchText(value);
    handleDebouncedTypeSearch(value);
  };

  const handleCategorySearchChange = (value) => {
    setIsCategorySearchLoading(true);
    setDebouncedCategorySearchText(value);
    handleDebouncedCategorySearch(value);
  };

  const handleSubCategorySearchChange = (value) => {
    setIsSubCategorySearchLoading(true);
    setDebouncedSubCategorySearchText(value);
    handleDebouncedSubCategorySearch(value);
  };

  const handleDebouncedTypeSearch = useDebouncedCallback((value) => {
    setTypeSearchText(value);
    setIsTypeSearchLoading(false);
  }, 1000);

  const handleDebouncedCategorySearch = useDebouncedCallback((value) => {
    setCategorySearchText(value);
    setIsCategorySearchLoading(false);
  }, 1000);

  const handleDebouncedSubCategorySearch = useDebouncedCallback((value) => {
    setSubCategorySearchText(value);
    setIsSubCategorySearchLoading(false);
  }, 1000);

  return (
    <>
      <Topbar title="Item Organization"></Topbar>
      {/*<Button
        className={"w-25 ms-auto me-4"}
        handleClick={() => addDefaultCategories(currentStore)}
        IconImage={AddIcon}
        label={"Add Default Types / Categories"}
      />*/}
      <Row className="m-0">
        <Col md={3}>
          <Row className="m-0">
            <Col md={12} className="mb-3">
              <SearchField
                value={debouncedTypeSearchText}
                IconImage={SearchIcon}
                label="Search Type"
                placeHolder="Type or Scan"
                clearSearchText={clearTypeSearchText}
                CrossIcon={typeSearchText ? CrossIcon : ""}
                onChange={(e) => handleTypeSearchChange(e.target.value)}
                LoadingIndicator={isTypeSearchLoading && LoadingIndicator}
              />
            </Col>
            <Col md={12} className="mb-3">
              <AGGridTable
                gridRef={gridRef}
                colDefs={TypeColDefs}
                getRowId={typeGetRowId}
                gridData={typesGridData}
                getRowStyle={getRowStyle}
                searchText={typeSearchText}
                defaultColDef={defaultColDef}
                pagination={false}
                tableHeight={`calc(100vh - 220px)`}
              />
            </Col>
            <Col md={12}>
              <Button
                className={"w-100"}
                handleClick={toggleCustomTypeModal}
                IconImage={AddIcon}
                label={buttonNameConstants.ADD_TYPE}
              />
            </Col>
          </Row>
        </Col>
        <Col md={4}>
          <Row className="m-0">
            <Col md={12} className="mb-3">
              <SearchField
                value={debouncedCategorySearchText}
                IconImage={SearchIcon}
                label="Search Category"
                placeHolder="Type or Scan"
                clearSearchText={clearCategorySearchText}
                CrossIcon={categorySearchText ? CrossIcon : ""}
                onChange={(e) => handleCategorySearchChange(e.target.value)}
                LoadingIndicator={isCategorySearchLoading && LoadingIndicator}
              />
            </Col>
            <Col md={12} className="mb-3">
              <AGGridTable
                gridRef={gridRef}
                pagination={false}
                getRowId={typeGetRowId}
                getRowStyle={getRowStyle}
                colDefs={CategoriesColDefs}
                gridData={categoriesGridData}
                defaultColDef={defaultColDef}
                searchText={categorySearchText}
                tableHeight={`calc(100vh - 220px)`}
              />
            </Col>
            <Col md={12}>
              <Button
                className={"w-100"}
                handleClick={toggleCustomCategoryModal}
                IconImage={AddIcon}
                label={buttonNameConstants.ADD_CATEGORY}
              />
            </Col>
          </Row>
        </Col>
        <Col md={5}>
          <Row className="m-0">
            <Col md={12} className="mb-3">
              <SearchField
                value={debouncedSubCategorySearchText}
                IconImage={SearchIcon}
                label="Search Sub-Category"
                placeHolder="Type or Scan"
                clearSearchText={clearSubCategorySearchText}
                CrossIcon={subCategorySearchText ? CrossIcon : ""}
                onChange={(e) => handleSubCategorySearchChange(e.target.value)}
                LoadingIndicator={
                  isSubCategorySearchLoading && LoadingIndicator
                }
              />
            </Col>
            <Col md={12} className="mb-3">
              <AGGridTable
                gridRef={gridRef}
                pagination={false}
                getRowId={typeGetRowId}
                getRowStyle={getRowStyle}
                defaultColDef={defaultColDef}
                colDefs={SubCategoriesColDefs}
                gridData={subCategoriesGridData}
                searchText={subCategorySearchText}
                tableHeight={`calc(100vh - 220px)`}
              />
            </Col>
            <Col md={12}>
              <Button
                className={"w-100"}
                handleClick={toggleCustomSubCategoryModal}
                IconImage={AddIcon}
                label={buttonNameConstants.ADD_SUB_CATEGORY}
              />
            </Col>
          </Row>
        </Col>
      </Row>
      <AddCustomType
        storeId={currentStore?.id}
        AddCustomType={addCustomProductTypes}
        typeModalFormData={typeModalFormData}
        isCustomTypeModal={isCustomTypeModal}
        setTypeModalFormData={setTypeModalFormData}
        toggleCustomTypeModal={toggleCustomTypeModal}
        updateCustomProductTypes={updateCustomProductTypes}
        companyId={currentStore?.companyId}
      />
      <AddCustomCategory
        customTypes={customTypes}
        storeId={currentStore?.id}
        AddCustomCategory={addCustomCategories}
        categoryModalFormData={categoryModalFormData}
        isCustomCategoryModal={isCustomCategoryModal}
        updateCustomCategories={updateCustomCategories}
        setCategoryModalFormData={setCategoryModalFormData}
        toggleCustomCategoryModal={toggleCustomCategoryModal}
        companyId={currentStore?.companyId}
      />
      <AddCustomSubCategory
        customTypes={customTypes}
        storeId={currentStore?.id}
        customCategories={customCategories}
        AddCustomSubCategory={addCustomSubCategories}
        subCategoryModalFormData={subCategoryModalFormData}
        isCustomSubCategoryModal={isCustomSubCategoryModal}
        updateCustomSubCategories={updateCustomSubCategories}
        setSubCategoryModalFormData={setSubCategoryModalFormData}
        toggleCustomSubCategoryModal={toggleCustomSubCategoryModal}
        companyId={currentStore?.companyId}
      />
    </>
  );
};

//-------Mapping the component's props to the reducer's state
const mapStateToProps = (state) => ({
  currentStore: state.store.currentStore,
  itemOrganization: state.itemOrganization,
});

//-------Mapping the component's props to the related actions
const mapDispatchToProps = (dispatch) => ({
  addCustomProductTypes: (storeId, data, handleHideModal, companyId) =>
    dispatch(
      itemOrganizationActions.addCustomProductTypes(
        storeId,
        data,
        handleHideModal,
        companyId
      )
    ),
  addCustomCategories: (storeId, data, handleHideModal, companyId) =>
    dispatch(
      itemOrganizationActions.addCustomCategories(
        storeId,
        data,
        handleHideModal,
        companyId
      )
    ),
  addCustomSubCategories: (storeId, data, handleHideModal, companyId) =>
    dispatch(
      itemOrganizationActions.addCustomSubCategories(
        storeId,
        data,
        handleHideModal,
        companyId
      )
    ),
  updateCustomProductTypes: (storeId, data, handleHideModal, companyId) =>
    dispatch(
      itemOrganizationActions.updateCustomProductTypes(
        storeId,
        data,
        handleHideModal,
        companyId
      )
    ),
  updateCustomCategories: (storeId, data, handleHideModal, companyId) =>
    dispatch(
      itemOrganizationActions.updateCustomCategories(
        storeId,
        data,
        handleHideModal,
        companyId
      )
    ),
  updateCustomSubCategories: (storeId, data, handleHideModal, companyId) =>
    dispatch(
      itemOrganizationActions.updateCustomSubCategories(
        storeId,
        data,
        handleHideModal,
        companyId
      )
    ),
  deleteCustomProductTypes: (storeId, data, companyId) =>
    dispatch(
      itemOrganizationActions.deleteCustomProductTypes(storeId, data, companyId)
    ),
  deleteCustomCategories: (storeId, categoryId, companyId) =>
    dispatch(
      itemOrganizationActions.deleteCustomCategories(
        storeId,
        categoryId,
        companyId
      )
    ),
  deleteCustomSubCategories: (storeId, data, companyId) =>
    dispatch(
      itemOrganizationActions.deleteCustomSubCategories(
        storeId,
        data,
        companyId
      )
    ),
});

//-------Export AddNewUser Component
export default connect(mapStateToProps, mapDispatchToProps)(ItemOrganization);
